import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';
import Logo12EN12 from '../components/Logo12EN12';
import BrandArrow from '../components/BrandArrow';
import { hoyLocal } from '../lib/horaEspana';
import { ajusteEnManosDelEquipo } from '../lib/revision';

// Pantalla de bienvenida tras completar el cuestionario inicial.
// Le "devuelve" al usuario el resultado de su esfuerzo (sus macros calculados)
// y lo empuja a su primer paso útil: preparar su día de comidas.

const MACRO = { protein: '#FF671F', carbs: '#2196F3', fat: '#FFA500' };

const getP = (m) => m?.protein || m?.proteinas || m?.proteina || 0;
const getH = (m) => m?.carbs || m?.hidratos || 0;
const getG = (m) => m?.fat || m?.grasas || m?.grasa || 0;

const MacroPill = ({ label, value, color }) => (
    <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-[#222222] bg-card px-4 py-5 flex-1 min-w-[92px]">
        <span className="font-data font-extrabold text-3xl md:text-4xl leading-none" style={{ color }}>{Math.round(value)}</span>
        <span className="text-muted-foreground text-[10px] uppercase font-bold tracking-wider mt-1">g</span>
        <span className="text-xs font-bold uppercase tracking-wide mt-2" style={{ color }}>{label}</span>
    </div>
);

const WelcomePage = () => {
    const navigate = useNavigate();
    const { profile, api, refreshProfile } = useAuth();

    // LA VUELTA DE LA PASARELA (19-08). El ajuste a medida de 87 € manda aquí con
    // `?ajuste=ok&session_id=...`, y aquí no lo miraba nadie: el cliente pagaba, Stripe
    // cobraba y su ficha se quedaba en «iniciado» -- sin la cola del lunes, sin aviso al
    // equipo y sin abrirle el cuestionario completo. En local no hay webhook que lo salve,
    // y en producción tampoco se puede depender solo de él: por eso existe esta llamada.
    // Comprobado pagando de verdad en el entorno de prueba de Stripe.
    const [avisandoDelPago, setAvisandoDelPago] = React.useState(false);
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sesion = params.get('session_id');
        if (!sesion) return;
        setAvisandoDelPago(true);
        api.post('/billing/checkout-session/sync', { session_id: sesion })
            .then(() => refreshProfile && refreshProfile())
            .catch((e) => console.error('[pago] no se pudo confirmar la sesión', e))
            .finally(() => {
                setAvisandoDelPago(false);
                // La dirección se limpia para que recargar no vuelva a lanzar la confirmación.
                window.history.replaceState({}, '', window.location.pathname);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Al entrar en el Inicio, el recorrido de la primera vez sale solo (doc 21-08,
    // apartado 23): esta pantalla ya no lo ofrece ni lo salta, solo deja pasar.
    const entrar = () => navigate('/dashboard');

    // EL DÍA DE ENTRENO CON EL PERIENTRENO DENTRO (ALT-13, 14-10). Aquí se pintaba
    // `macros_training`, que son solo las comidas, y dos pantallas después el Inicio y Mis
    // macros enseñan el día entero: 190 · 140 aquí y 235 · 180 allí. El total lo da el
    // servidor (`dia_entreno` de /macros/historial, el mismo número de Mis macros); mientras
    // llega no se pinta ninguno, y si no llega se queda el de la ficha.
    const sinAcceso = profile?.acceso?.activo === false;
    const [delDia, setDelDia] = React.useState(undefined);
    React.useEffect(() => {
        if (!profile || sinAcceso) return undefined;
        let vivo = true;
        api.get(`/macros/historial?dia=${hoyLocal()}`)
            .then(r => { if (vivo) setDelDia(r.data?.vigente?.dia_entreno || null); })
            .catch((e) => { console.error('[bienvenida] no se pudo leer el día de entreno', e); if (vivo) setDelDia(null); });
        return () => { vivo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- una vez, al tener la ficha
    }, [!!profile, sinAcceso]);

    // SIN PLAN NO HAY MACROS QUE ENSEÑAR (ALT-09, 14-10): escribiendo la dirección a mano,
    // a una cuenta sin plan le salía «Tus macros están calculados». Al Inicio, que ya sabe
    // qué decirle a cada uno (sin plan, sin pagar o caducado). Con la vuelta de un pago en
    // la dirección se espera a que se confirme.
    if (sinAcceso && !avisandoDelPago && !new URLSearchParams(window.location.search).get('session_id')) {
        return <Navigate to="/dashboard" replace />;
    }

    const mt = (delDia && getP(delDia) > 0) ? delDia : profile?.macros_training;
    const hasMacros = mt && (getP(mt) > 0);
    const esperandoElDia = delDia === undefined && hasMacros;

    return (
        <div className="min-h-screen bg-background relative overflow-hidden flex flex-col" data-testid="welcome-page">
            {/* Glow de marca */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand/10 rounded-full blur-[150px]" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-brand/5 rounded-full blur-[120px]" />
            <BrandArrow className="absolute -right-16 -bottom-16 w-[420px] h-[420px] text-brand/[0.04] pointer-events-none" />

            {/* Cabecera */}
            <div className="relative z-10 flex items-center h-16 px-6 md:px-10">
                <Logo12EN12 size="sm" />
            </div>

            <div className="flex-1 flex items-center justify-center p-6 relative z-10">
                <div className="w-full max-w-xl text-center">
                    {/* Mientras se confirma el pago, que sepa que estamos en ello: si no, ve
                        su pantalla de siempre y no sabe si su dinero ha llegado. */}
                    {avisandoDelPago && (
                        <p className="caption text-brand mb-2" data-testid="confirmando-pago">
                            Confirmando tu pago...
                        </p>
                    )}
                    {/* Sin fecha (Francisco, 30-10): el lunes de la cola no coincidía con las 48
                        horas del aviso del equipo, y el plazo es cosa del equipo. */}
                    {!avisandoDelPago && ajusteEnManosDelEquipo(profile) && (
                        <p className="caption text-brand mb-2" data-testid="ajuste-pagado">
                            Tu ajuste a medida está pagado. El equipo lo está preparando.
                        </p>
                    )}
                    {/* La ceja «Tus macros» en vez de «Todo listo, {nombre}» (20-10, prompt del
                        rediseño del cuestionario, punto 35). */}
                    <p className="caption text-brand mb-2">Tus macros</p>
                    <h1 className="font-heading font-bold text-4xl md:text-5xl uppercase tracking-tight text-foreground mb-3 leading-none">
                        {hasMacros ? 'Tus macros están calculados' : 'Ya casi está'}
                    </h1>
                    <p className="text-muted-foreground mb-8 text-sm md:text-base max-w-md mx-auto">
                        {/* «Armamos» así es de Argentina y en España se lee raro; y
                            «personalizados» es palabra de folleto que sobra cuando ya le estás
                            enseñando sus tres números (Jesús, 11-08). */}
                        Con tus respuestas hemos creado tus objetivos. Estos son tus
                        números para un <span className="text-foreground font-semibold">día de entrenamiento</span>:
                    </p>

                    {esperandoElDia ? (
                        <div className="mb-10 h-[150px]" aria-hidden="true" />
                    ) : hasMacros ? (
                        <>
                            {/* Sin las kcal / día (18-10): se le enseñan sus tres macros, que es
                                con lo que trabaja; las calorías no le dicen qué comer. */}
                            <div className="flex gap-3 justify-center mb-10">
                                <MacroPill label="Proteína" value={getP(mt)} color={MACRO.protein} />
                                <MacroPill label="Hidratos" value={getH(mt)} color={MACRO.carbs} />
                                <MacroPill label="Grasa" value={getG(mt)} color={MACRO.fat} />
                            </div>
                        </>
                    ) : (
                        <div className="mb-10 surface p-5 text-sm text-muted-foreground">
                            Estamos terminando de calcular tus macros. Podrás verlos en tu panel en unos instantes.
                        </div>
                    )}

                    <div className="flex justify-center">
                        <Button onClick={entrar} data-testid="welcome-skip-btn"
                            className="bg-brand hover:bg-brand/90 text-white font-bold uppercase tracking-wider px-8 py-6 text-base">
                            Entrar <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WelcomePage;
